import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Typography, Button, CheckBox } from '../../../../components';
import { icons } from '../../../../icons';
import { HeadLine3 } from '../../../../components/typography/styled';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  padding: 20px;
  gap: 20px;
  min-width: 400px;
  max-width: 500px;
  background: ${({theme}) => theme.palette[10]};
  box-shadow: 0px 1px 2px rgba(0, 0, 0, 0.1);
  border-radius: 8px;
`;

const HeadWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Icon = styled.img`
  width: 24px;
  height: 24px;
  cursor: pointer;
  &:hover {
    opacity: 0.7;
  }
`;

const BodyModal = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const WarningText = styled.div`
  color: #d32f2f;
  font-size: 14px;
  margin-top: 4px;
`;

const OptionContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px;
  background: ${({ theme }) => theme.grey?.[100] || '#f5f5f5'};
  border-radius: 8px;
`;

const ButtonsWrapper = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 12px;
`;

interface ServiceDeleteModalProps {
  service: any;
  onDelete: (deleteOnlyFromDb: boolean, deleteAssociatedResources: boolean) => void;
  onCancel: () => void;
  loading?: boolean;
}

const ServiceDeleteModal: FC<ServiceDeleteModalProps> = ({
  service,
  onDelete,
  onCancel,
  loading = false,
}) => {
  const [t] = useTranslation();
  const [deleteOnlyFromDb, setDeleteOnlyFromDb] = useState(false);
  const [deleteWithResources, setDeleteWithResources] = useState(false);

  // Check if service has associated resources (not in pending, canceled, terminated, fraud status)
  const hasAssociatedResources = !['pending', 'canceled', 'terminated', 'fraud'].includes(service.status);

  const handleDelete = () => {
    if (hasAssociatedResources) {
      // Need to select an option
      if (!deleteOnlyFromDb && !deleteWithResources) {
        return; // Don't proceed without selection
      }
      onDelete(deleteOnlyFromDb, deleteWithResources);
    } else {
      // Simple delete
      onDelete(false, false);
    }
  };

  const canDelete = !hasAssociatedResources || deleteOnlyFromDb || deleteWithResources;

  return (
    <Container>
      <HeadWrapper>
        <HeadLine3 style={{ marginLeft: '10px', marginTop: '10px' }}>
          {t('DELETE_SERVICE')} #{service.id}
        </HeadLine3>
        <Icon onClick={onCancel} src={icons.CloseIcon} />
      </HeadWrapper>
      <BodyModal>
        {hasAssociatedResources ? (
          <>
            <Typography variant="body">
              {t('DELETE_SERVICE_HAS_RESOURCES')}
            </Typography>
            <Typography variant="body" customStyles={{ color: '#d32f2f', fontWeight: 'bold' }}>
              {t('UNSETTLED_USAGE_NOTE')}
            </Typography>
            <OptionContainer>
              <CheckBox
                title={t('DELETE_ONLY_FROM_DB')}
                value={deleteOnlyFromDb}
                onChange={() => {
                  setDeleteOnlyFromDb(!deleteOnlyFromDb);
                  if (!deleteOnlyFromDb) setDeleteWithResources(false);
                }}
              />
              {deleteOnlyFromDb && (
                <WarningText>{t('DELETE_ONLY_FROM_DB_WARNING')}</WarningText>
              )}
            </OptionContainer>
            <OptionContainer>
              <CheckBox
                title={t('DELETE_WITH_RESOURCES')}
                value={deleteWithResources}
                onChange={() => {
                  setDeleteWithResources(!deleteWithResources);
                  if (!deleteWithResources) setDeleteOnlyFromDb(false);
                }}
              />
              {deleteWithResources && (
                <WarningText>{t('DELETE_WITH_RESOURCES_WARNING')}</WarningText>
              )}
            </OptionContainer>
          </>
        ) : (
          <>
            <Typography variant="body">
              {t('DELETE_SERVICE_CONFIRM', { name: service.display_name || service.product?.name })}
            </Typography>
            <Typography variant="body" customStyles={{ color: '#d32f2f', fontWeight: 'bold' }}>
              {t('DELETE_DATA_WARNING')}
            </Typography>
          </>
        )}

        <ButtonsWrapper>
          <Button
            variant="stroke"
            size="display"
            onClick={onCancel}
            disabled={loading}
          >
            {t('CANCEL')}
          </Button>
          <Button
            variant="primary"
            size="display"
            onClick={handleDelete}
            loading={loading}
            disabled={!canDelete}
            customStyles={{ backgroundColor: '#d32f2f' }}
          >
            {t('DELETE_SERVICE')}
          </Button>
        </ButtonsWrapper>
      </BodyModal>
    </Container>
  );
};

export default ServiceDeleteModal;
